import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { LayoutDashboard, Briefcase, Users, UserCog, Package, FileCheck2, Settings, LogOut, AlertCircle, ShoppingCart, PhoneCall } from "lucide-react";
import Logo from "@/components/site/Logo";
import { useAuth } from "@/context/AuthContext";
import { fetchOverdueInvoiceNotifications, fetchCompanyUsage } from "@/lib/api";

const ITEMS = [
    { to: "/dashboard", label: "Overview", icon: LayoutDashboard, end: true, id: "side-overview" },
    { to: "/dashboard/jobs", label: "Jobs", icon: Briefcase, id: "side-jobs" },
    { to: "/dashboard/customers", label: "Customers", icon: Users, id: "side-customers" },
    { to: "/dashboard/employees", label: "Employees", icon: UserCog, id: "side-employees" },
    { to: "/dashboard/materials", label: "Materials", icon: Package, id: "side-materials" },
    {
        to: "/dashboard/indkoebsliste",
        label: "Indkøb",
        icon: ShoppingCart,
        id: "side-indkoeb",
        activeMatch: ["/dashboard/indkoebsliste", "/dashboard/leveringscheckliste"],
    },
    { to: "/dashboard/faktura-klar", label: "Faktura-klar", icon: FileCheck2, id: "side-faktura" },
    { to: "/dashboard/settings", label: "Settings", icon: Settings, id: "side-settings" },
];

function UsageMeter() {
    const [u, setU] = useState(null);
    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            try {
                const r = await fetchCompanyUsage();
                if (!cancelled) setU(r);
            } catch { /* sidebar må ikke crashe */ }
        };
        load();
        const t = setInterval(load, 60_000);
        return () => { cancelled = true; clearInterval(t); };
    }, []);
    if (!u) return null;
    const fmt = (m) => (Number(m) || 0).toLocaleString("da-DK", { minimumFractionDigits: 1, maximumFractionDigits: 1 });
    const usedSec = u.used_seconds ?? (u.used_minutes || 0) * 60;
    const limitSec = u.monthly_limit_seconds ?? (u.included_minutes || 0) * 60;
    const remSec = u.monthly_remaining_seconds ?? Math.max(0, limitSec - usedSec);
    const topupSec = u.topup_remaining_seconds ?? (u.topup_remaining_minutes || 0) * 60;
    const incMin = limitSec > 0 ? limitSec / 60 : (u.included_minutes || 0);
    const usedMin = usedSec / 60;
    const remMin = remSec / 60;
    const pct = incMin > 0 ? Math.min(100, Math.round((usedMin / incMin) * 100)) : 0;
    const barColor = pct >= 100 ? "bg-rose-400" : pct >= 80 ? "bg-amber-400" : "bg-blue-400";
    return (
        <div className="px-3 mb-3" data-testid="sidebar-usage">
            <p className="text-xs text-slate-400 uppercase tracking-wider">Forbrug</p>
            <div className="mt-1 flex items-center gap-1.5 text-slate-600">
                <PhoneCall className="w-3.5 h-3.5 text-slate-400" />
                <span className="text-xs font-medium">AI-opkald</span>
            </div>
            <p className="mt-1 text-sm font-semibold text-slate-900 tabular-nums" data-testid="sidebar-usage-count">
                {fmt(usedMin)} / {Math.round(incMin)} min.
            </p>
            <div className="mt-1.5 h-1.5 w-full rounded-full bg-slate-100 overflow-hidden">
                <div className={`h-full rounded-full ${barColor} opacity-70 transition-all`} style={{ width: `${pct}%` }} data-testid="sidebar-usage-bar" />
            </div>
            <p className="mt-1 text-[11px] text-slate-400" data-testid="sidebar-usage-remaining">{fmt(remMin)} min. tilbage</p>
            {topupSec > 0 && (
                <p className="mt-0.5 text-[11px] text-slate-400" data-testid="sidebar-usage-topup">Ekstra saldo: {fmt(topupSec / 60)} min.</p>
            )}
        </div>
    );
}

function OverdueBadge() {
    const navigate = useNavigate();
    const [data, setData] = useState({ count: 0, items: [] });

    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            try {
                const r = await fetchOverdueInvoiceNotifications();
                if (!cancelled) setData(r || { count: 0, items: [] });
            } catch {
                // Silently ignore — sidebar shouldn't break on transient API hiccups
            }
        };
        load();
        const interval = setInterval(load, 60_000);
        return () => { cancelled = true; clearInterval(interval); };
    }, []);

    if (!data.count) return null;
    const first = data.items[0];
    return (
        <button
            onClick={() => navigate("/dashboard/faktura-klar?filter=overdue")}
            data-testid="sidebar-overdue-badge"
            className="w-full text-left px-3 py-2.5 mb-3 rounded-xl border border-red-200 bg-red-50 hover:bg-red-100 transition group"
        >
            <div className="flex items-start gap-2">
                <div className="relative shrink-0 mt-0.5">
                    <AlertCircle className="w-4 h-4 text-red-600" />
                    <span className="absolute -top-1 -right-1 w-2 h-2 rounded-full bg-red-600 animate-pulse" />
                </div>
                <div className="flex-1 min-w-0">
                    <p className="text-[11px] font-bold text-red-700">
                        {data.count === 1 ? "1 faktura er forfalden" : `${data.count} fakturaer er forfaldne`}
                    </p>
                    {first && (
                        <p className="text-[10px] text-red-600 truncate" data-testid="sidebar-overdue-first">
                            Faktura {first.number || first.id?.slice(0, 8)} mangler betaling
                        </p>
                    )}
                </div>
            </div>
        </button>
    );
}

export default function Sidebar({ onNavigate }) {
    const navigate = useNavigate();
    const location = useLocation();
    const { user, logout } = useAuth();

    const handleLogout = () => {
        logout();
        navigate("/", { replace: true });
    };

    return (
        <aside data-testid="dashboard-sidebar" className="h-full w-64 bg-white border-r border-slate-200/70 flex flex-col">
            <div className="px-5 py-4 flex items-center justify-center border-b border-slate-100">
                <Logo to="/dashboard" height={60} />
            </div>

            <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto" aria-label="Dashboard navigation">
                {ITEMS.map((item) => (
                    <NavLink
                        key={item.to}
                        to={item.to}
                        end={item.end}
                        data-testid={item.id}
                        onClick={onNavigate}
                        className={({ isActive }) => {
                            const active = isActive || (item.activeMatch || []).some((p) => location.pathname.startsWith(p));
                            return `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors ${
                                active
                                    ? "bg-gradient-to-r from-blue-50 to-purple-50 text-blue-700 shadow-sm"
                                    : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
                            }`;
                        }}
                    >
                        <item.icon className="w-4 h-4" />
                        {item.label}
                    </NavLink>
                ))}
            </nav>

            <div className="px-3 py-4 border-t border-slate-100">
                <OverdueBadge />
                {user && (
                    <div className="px-3 mb-3">
                        <p className="text-xs text-slate-400 uppercase tracking-wider">Logget ind som</p>
                        <p className="text-sm font-semibold text-slate-900 truncate">{user.name}</p>
                        <p className="text-xs text-slate-500 truncate">{user.email}</p>
                        <span className="inline-block mt-1.5 text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full bg-blue-50 text-blue-700">{user.role}</span>
                    </div>
                )}
                {user && <UsageMeter />}
                <button
                    onClick={handleLogout}
                    data-testid="sidebar-logout-btn"
                    className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-slate-600 hover:bg-red-50 hover:text-red-600 transition-colors"
                >
                    <LogOut className="w-4 h-4" />
                    Log ud
                </button>
            </div>
        </aside>
    );
}
